import { IGetScrollDataRequestParams } from "Dashboard/scenes/Chat/domain/IGetScrollDataRequestParams";
import { useCallback, useEffect, useState } from "react";

export default function useDataFetch<T>(
  getData: (
    params: IGetScrollDataRequestParams,
    signal: AbortSignal
  ) => Promise<T[]>,
  limit: number,
  afterId?: number,
  beforeId?: number,
  getItemId?: (item: T) => number,
  filter?: object,
) {
  const [loadingAfter, setLoadingAfter] = useState(true);
  const [hasMoreAfter, setHasMoreAfter] = useState(false);
  const [loadingBefore, setLoadingBefore] = useState(false);
  const [hasMoreBefore, setHasMoreBefore] = useState(true);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC' | undefined>(undefined);
  const [error, setError] = useState(false);
  const [data, setData] = useState<T[]>([]);

  useEffect(() => {
    const abortCtrl = new AbortController();
    const fetchData = async () => {
      try {
        setLoadingAfter(true);
        setError(false);
        const newData = await getData({ limit, afterId, sortColumn, sortOrder, filter }, abortCtrl.signal);
        setData((prevData) => {
          if (getItemId) {
            const newDataIds = newData.map(item => getItemId(item));
            const prevDataFiltered = prevData.filter(item => !newDataIds.includes(getItemId(item)));
            return [...prevDataFiltered, ...newData];
          } else {
            return [...prevData, ...newData];
          }
        });
        setHasMoreAfter(newData.length >= limit);
        setLoadingAfter(false);
      } catch (err) {
        setError(true);
      }
    };
    fetchData();
    return () => {
      abortCtrl.abort();
    };
  }, [getData, limit, afterId, sortColumn, sortOrder]);

  useEffect(() => {
    const abortCtrl = new AbortController();
    const fetchData = async () => {
      try {
        setLoadingBefore(true);
        setError(false);
        const newData = await getData({ limit, beforeId, sortColumn, sortOrder, filter }, abortCtrl.signal);
        setData((prevData) => {
          if (getItemId) {
            const newDataIds = newData.map(item => getItemId(item));
            const prevDataFiltered = prevData.filter(item => !newDataIds.includes(getItemId(item)));
            return [...newData, ...prevDataFiltered];
          } else {
            return [...newData, ...prevData];
          }
        });
        setHasMoreBefore(newData.length >= limit);
        setLoadingBefore(false);
      } catch (err) {
        setError(true);
      }
    };
    if (beforeId) {
      fetchData();
    }
    return () => {
      if (beforeId) {
        abortCtrl.abort();
      }
    };
  }, [getData, limit, beforeId]);

  const doSortBy = useCallback((newSortColumn: string) => {
    if (sortColumn === newSortColumn) {
      if (sortOrder === 'ASC') {
        setSortOrder('DESC');
      } else {
        setSortOrder('ASC');
      }
    } else {
      setSortColumn(newSortColumn);
      setSortOrder('ASC');
    }
  }, [sortColumn, sortOrder, setSortColumn, setSortOrder]);
  return {
    data,
    setData,
    hasMoreAfter,
    loadingAfter,
    hasMoreBefore,
    loadingBefore,
    doSortBy,
    sortColumn,
    sortOrder,
    error,
  };
}
